/**
 * © 2016 Becki Authors. See the AUTHORS file found in the top-level directory
 * of this distribution.
 */

import { Core, EditorRenderer } from 'the-grid';
import { Component, AfterViewInit, Output, ViewChild, ElementRef, EventEmitter, NgZone, OnDestroy } from '@angular/core';
import { ModalService } from '../services/ModalService';
import { ModalsGridConfigPropertiesModel } from '../modals/grid-config-properties';
import { IWidgetVersion } from '../backend/TyrionAPI';

@Component({
    selector: 'bk-grid-view',
    template: `
    <div #screens></div>
`
})
export class GridViewComponent implements AfterViewInit, OnDestroy {

    @ViewChild('screens')
    screens: ElementRef;

    @Output()
    onRequestWidgetSource = new EventEmitter<{type: any, resolve: (name: string) => void}>();

    @Output()
    onWidgetLog = new EventEmitter<{id: number, type: string, message: string}>();

    @Output()
    onWidgetMessage = new EventEmitter<{id: number, message: any}>();

    @Output()
    onWidgetError = new EventEmitter<{id: number, error: any}>();

    @Output()
    onWidgetDrag = new EventEmitter<{e: any, outside: boolean}>();

    @Output()
    onChange: EventEmitter<{}> = new EventEmitter<{}>();

    protected gridController: Core.Controller;

    protected gridRenderer: EditorRenderer.ControllerRenderer;

    constructor(protected modalService: ModalService, protected zone: NgZone) {
        this.zone.runOutsideAngular(() => {
            this.gridController = new Core.Controller();

            this.gridController.registerRequestWidgetSourceCallback((type, resolve) => {
                this.onRequestWidgetSource.emit({
                    type: type,
                    resolve: resolve
                });
            });

            /*
            this.gridController.registerDataChangedCallback(() => {
                // this.modal.closeModal(false);
                // console.log("CHANGED");
                // this.modelChange.emit(this.controller.getDataJson());
            });
            */
            Core.Controller.cleanWidgetIONameCounter();
        });
    }

    requestCreateWidget(type: any, event?: MouseEvent): EditorRenderer.WidgetDragHandler {
        let r: EditorRenderer.WidgetDragHandler = null;
        this.zone.runOutsideAngular(() => {
            r = (<EditorRenderer.ControllerRenderer>this.gridController.renderer).requestCreateWidget(type, event);
        });
        return r;
    }

    ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => {
            this.gridRenderer = new EditorRenderer.ControllerRenderer(this.gridController, this.screens.nativeElement, false, this.handleOnWidgetError, this.handleOnWidgetMessage, this.handleOnWidgetLog);
            this.gridRenderer.registerOpenConfigCallback(widget => {
                let versions: IWidgetVersion[] = null;
                this.zone.run(() => {
                    let m = new ModalsGridConfigPropertiesModel(widget, this.gridController, versions);
                    this.modalService.showModal(m);
                    this.onChange.emit({});
                });
            });
            this.gridRenderer.anyChangeCallback = () => {
                this.onChange.emit({});
            };
            this.gridController.setRenderer(this.gridRenderer);
            this.gridRenderer.widgetDraggingCallback = this.onWidgetDragMoveEvent;
        });
    }

    onWidgetDragMoveEvent = (e: any, outside: boolean) => {
        this.zone.run(() => {
            this.onWidgetDrag.emit({
                e: e,
                outside: outside
            });
        });
    }

    ngOnDestroy(): void {
        this.zone.runOutsideAngular(() => {
            this.gridController.destroy();
        });
    }

    addPage(): void {
        this.zone.runOutsideAngular(() => {
            this.gridController.addPage();
        });
    }

    handleOnWidgetError = (id: number, error: any) => {
        this.onWidgetError.emit({
            id: id,
            error: error
        });
    }

    handleOnWidgetMessage = (id: number, message: any) => {
        this.onWidgetMessage.emit({
            id: id,
            message: message
        });
    }

    handleOnWidgetLog = (id: number, type: string, message: string) => {
        this.onWidgetLog.emit({
            id: id,
            type: type,
            message: message
        });
    }

    getDataJson(): string {
        let r: string = null;
        this.zone.runOutsideAngular(() => {
            r = this.gridController.getDataJson();
        });
        return r;
    }

    setDataJson(data: string): void {
        this.zone.runOutsideAngular(() => {
            this.gridController.setDataJson(data);
        });
    }

    setDeviceProfile(deviceProfile: string): void {
        this.zone.runOutsideAngular(() => {
            this.gridController.setDeviceProfileByName(deviceProfile);
        });
    }

    getDeviceProfile(): string {
        let r: string = null;
        this.zone.runOutsideAngular(() => {
            r = this.gridController.deviceProfile.name;
        });
        return r;
    }

    getInterfaceJson(): string {

        let out: any = {
            analogInputs: {},
            digitalInputs: {},
            messageInputs: {},
            analogOutputs: {},
            digitalOutputs: {},
            messageOutputs: {},
        };

        this.zone.runOutsideAngular(() => {
            const gridInterface = this.gridController.getInterface();

            if (gridInterface.analogInputs) {
                for (let k in gridInterface.analogInputs) {
                    if (!gridInterface.analogInputs.hasOwnProperty(k)) { continue; }
                    let name = gridInterface.analogInputs[k].externalName;
                    if (!out.analogInputs[name]) {
                        out.analogInputs[name] = {};
                    }
                }
            }

            if (gridInterface.digitalInputs) {
                for (let k in gridInterface.digitalInputs) {
                    if (!gridInterface.digitalInputs.hasOwnProperty(k)) { continue; }
                    let name = gridInterface.digitalInputs[k].externalName;
                    if (!out.digitalInputs[name]) {
                        out.digitalInputs[name] = {};
                    }
                }
            }

            if (gridInterface.messageInputs) {
                for (let k in gridInterface.messageInputs) {
                    if (!gridInterface.messageInputs.hasOwnProperty(k)) { continue; }
                    let name = gridInterface.messageInputs[k].externalName;
                    if (!out.messageInputs[name]) {
                        out.messageInputs[name] = {
                            messageTypes: gridInterface.messageInputs[k].messageTypes
                        };
                    }
                }
            }

            if (gridInterface.analogOutputs) {
                for (let k in gridInterface.analogOutputs) {
                    if (!gridInterface.analogOutputs.hasOwnProperty(k)) { continue; }
                    let name = gridInterface.analogOutputs[k].externalName;
                    if (!out.analogOutputs[name]) {
                        out.analogOutputs[name] = {};
                    }
                }
            }

            if (gridInterface.digitalOutputs) {
                for (let k in gridInterface.digitalOutputs) {
                    if (!gridInterface.digitalOutputs.hasOwnProperty(k)) { continue; }
                    let name = gridInterface.digitalOutputs[k].externalName;
                    if (!out.digitalOutputs[name]) {
                        out.digitalOutputs[name] = {};
                    }
                }
            }

            if (gridInterface.messageOutputs) {
                for (let k in gridInterface.messageOutputs) {
                    if (!gridInterface.messageOutputs.hasOwnProperty(k)) { continue; }
                    let name = gridInterface.messageOutputs[k].externalName;
                    if (!out.messageOutputs[name]) {
                        out.messageOutputs[name] = {
                            messageTypes: gridInterface.messageOutputs[k].messageTypes
                        };
                    }
                }
            }
        });

        return JSON.stringify(out);
    }

}
